import type { AllowedCartItem, DefaultCartMeta } from '../types/cart';

/**
 * A composable that provides cart sorting functionality.
 * Sorts cart items by group order (based on first appearance) and then by position within each group.
 * Items without a position are placed at the end of their group.
 *
 * @param items - A ref to the array of cart items
 * @returns An object with the sorted items
 *
 * @example
 * const items = ref([
 *   { id: '1', group: 'subscription', position: 1 },
 *   { id: '2', group: 'fees', position: 0 },
 *   { id: '3', group: 'subscription', position: 0 }
 * ]);
 * const { sortedItems } = useCartSorting(items);
 * // sortedItems.value returns items ordered by: subscription (pos 0), subscription (pos 1), fees (pos 0)
 */
export function useCartSorting<TExtra extends DefaultCartMeta = DefaultCartMeta>(items: Ref<Array<AllowedCartItem<TExtra>>>) {
  const sortedItems = computed((): Array<AllowedCartItem<TExtra>> => {
    const groupOrder = buildGroupOrderMap(items.value);

    return [...items.value].sort((itemA, itemB) => {
      const groupComparison = compareByGroup(itemA, itemB, groupOrder);
      if (groupComparison !== 0) {
        return groupComparison;
      }
      return compareByPosition(itemA, itemB);
    });
  });

  return {
    sortedItems,
  };
}

/**
 * Builds a map of group to first occurrence index for maintaining group order.
 * @param items - Array of cart items
 * @returns Map of group name to its first occurrence index
 */
function buildGroupOrderMap<TExtra extends DefaultCartMeta= DefaultCartMeta>(items: Array<AllowedCartItem<TExtra>>): Map<string | undefined, number> {
  const groupOrder = new Map<string | undefined, number>();
  items.forEach((item, index) => {
    if (!groupOrder.has(item.group)) {
      groupOrder.set(item.group, index);
    }
  });
  return groupOrder;
}

/**
 * Compares two items by their group order (based on first appearance).
 * @param itemA - First item to compare
 * @param itemB - Second item to compare
 * @param groupOrder - Map of group names to their first occurrence index
 * @returns Negative if A comes first, positive if B comes first, 0 if same group
 */
function compareByGroup<TExtra extends DefaultCartMeta = DefaultCartMeta>(
  itemA: AllowedCartItem<TExtra>,
  itemB: AllowedCartItem<TExtra>,
  groupOrder: Map<string | undefined, number>,
): number {
  const groupOrderA = groupOrder.get(itemA.group) ?? Number.MAX_SAFE_INTEGER;
  const groupOrderB = groupOrder.get(itemB.group) ?? Number.MAX_SAFE_INTEGER;
  return groupOrderA - groupOrderB;
}

/**
 * Compares two items by their position within the same group.
 * @param itemA - First item to compare
 * @param itemB - Second item to compare
 * @returns Negative if A comes first, positive if B comes first, 0 if same position
 */
function compareByPosition<TExtra extends DefaultCartMeta= DefaultCartMeta>(
  itemA: AllowedCartItem<TExtra>,
  itemB: AllowedCartItem<TExtra>,
): number {
  const posA = itemA.position ?? Number.MAX_SAFE_INTEGER;
  const posB = itemB.position ?? Number.MAX_SAFE_INTEGER;
  return posA - posB;
}
